// src/pages/ChatBot/evals/run-detail.tsx
//
// Renders a single evaluation run's full document for the Evaluation tab. Split
// out of EvalsPanel to keep that file focused on orchestration (golden set +
// run lifecycle + polling). One entry point, RunDetail, switches on run.status:
//   running   → progress bar + done/total + spinner
//   failed    → red banner with run.error, plus the summary strip
//   completed → summary strip + retrieval metric cards + judge card(s) + a
//               collapsible per-query table (rows expand to the run's answer).
//
// Retrieval metric names are rendered generically (prettifyMetricKey): the
// camelCase base is spaced/capitalised and any "@budget" suffix is preserved,
// so budget-suffixed keys like "recallAtBudget@4096" need no hardcoding.

import { useState } from 'react';
import { formatDateTime } from '../query-logs/derive';
import type { EvalQueryResult, EvalRunDoc, JudgeMetrics } from './types';

// Judge scores are on a 1-5 scale; abstention accuracy is a 0-1 ratio.
const JUDGE_SCALE_MAX = 5;

interface RunDetailProps {
  run: EvalRunDoc | null;
  isLoading: boolean;
  error: string;
}

// Formats a metric to a fixed number of decimals; em-dash for missing values.
function formatMetric(value: number | undefined, digits = 3): string {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return value.toFixed(digits);
}

// Prettifies an open metric key: split the "@budget" suffix off, space the
// camelCase base and capitalise its first letter, then re-attach the suffix —
// "recallAtBudget@4096" → "Recall At Budget @4096".
function prettifyMetricKey(key: string): string {
  const [base, ...rest] = key.split('@');
  const suffix = rest.length > 0 ? ` @${rest.join('@')}` : '';
  const spaced = base
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim();
  const titled = spaced.charAt(0).toUpperCase() + spaced.slice(1);
  return `${titled}${suffix}`;
}

// Compact one-liner describing the scored pipeline, e.g.
// "hybrid · llm-judge · top 10 · 4000 tok · gpt-4o".
function pipelineSummary(run: EvalRunDoc): string {
  const { approach, reranker, finalTopK, contextTokenBudget, answerModel } = run.pipelineConfig;
  return `${approach} · ${reranker} · top ${finalTopK} · ${contextTokenBudget} tok · ${answerModel}`;
}

function SummaryStrip({ run }: { run: EvalRunDoc }) {
  const metrics = run.metrics;
  return (
    <div className="eval-summary">
      {metrics && (
        <span className="eval-summary-item">
          <strong>{metrics.nScored}</strong> / {metrics.nQueries} scored
        </span>
      )}
      {metrics && metrics.nUnreachable > 0 && (
        <span className="eval-summary-item eval-summary-note">
          {metrics.nUnreachable} unreachable
        </span>
      )}
      <span className="eval-summary-item">Started {formatDateTime(run.startedAt)}</span>
      {run.finishedAt && (
        <span className="eval-summary-item">Finished {formatDateTime(run.finishedAt)}</span>
      )}
      <span className="eval-summary-item eval-summary-config" title={pipelineSummary(run)}>
        {pipelineSummary(run)}
      </span>
    </div>
  );
}

function JudgeCard({ judge }: { judge: JudgeMetrics }) {
  const abstentionPct = `${(judge.abstentionAccuracy * 100).toFixed(0)}%`;
  return (
    <div className="eval-judge-card">
      <div className="eval-judge-row">
        <span className="eval-judge-label">Faithfulness</span>
        <span className="eval-judge-value">
          {formatMetric(judge.meanFaithfulness, 2)} / {JUDGE_SCALE_MAX}
          <span className="eval-judge-n"> (n={judge.nFaithfulness})</span>
        </span>
      </div>
      <div className="eval-judge-row">
        <span className="eval-judge-label">Correctness</span>
        <span className="eval-judge-value">
          {formatMetric(judge.meanCorrectness, 2)} / {JUDGE_SCALE_MAX}
          <span className="eval-judge-n"> (n={judge.nCorrectness})</span>
        </span>
      </div>
      <div className="eval-judge-row">
        <span className="eval-judge-label">Abstention accuracy</span>
        <span className="eval-judge-value">{abstentionPct}</span>
      </div>
      {judge.nFailures > 0 && (
        <p className="eval-judge-warn">
          {judge.nFailures} judge {judge.nFailures === 1 ? 'call' : 'calls'} failed — scores
          cover the {judge.nJudged} judged answer{judge.nJudged === 1 ? '' : 's'}.
        </p>
      )}
    </div>
  );
}

function QueryRow({ query }: { query: EvalQueryResult }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const unreachable = query.unreachable === true;
  return (
    <>
      <tr className="eval-query-row" onClick={() => setIsExpanded((open) => !open)}>
        <td>
          <div className="eval-query-question" title={query.question}>
            <span className="eval-query-caret">{isExpanded ? '▾' : '▸'}</span>
            {query.question}
          </div>
        </td>
        <td className="eval-query-tags">
          {query.abstained && <span className="eval-chip eval-chip-abstained">abstained</span>}
          {query.error && (
            <span className="eval-chip eval-chip-error" title={query.error}>
              error
            </span>
          )}
        </td>
        <td className="eval-num">
          {unreachable ? (
            <span
              className="eval-chip eval-chip-unlabeled"
              title="No labelled gold chunks in the index — retrieval is not scored."
            >
              unlabeled
            </span>
          ) : (
            formatMetric(query.recall, 3)
          )}
        </td>
        <td className="eval-num">{unreachable ? '' : formatMetric(query.ndcg, 3)}</td>
        <td className="eval-num">{unreachable ? '' : formatMetric(query.mrr, 3)}</td>
        <td className="eval-num">{formatMetric(query.faithfulness, 2)}</td>
        <td className="eval-num">{formatMetric(query.correctness, 2)}</td>
      </tr>
      {isExpanded && (
        <tr className="eval-query-detail-row">
          <td colSpan={7}>
            <div className="eval-query-answer">
              {query.answer ? query.answer : <em className="eval-muted">No answer text.</em>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function RunDetail({ run, isLoading, error }: RunDetailProps) {
  if (error) {
    return <div className="eval-run-error">{error}</div>;
  }
  if (!run && isLoading) {
    return (
      <div className="eval-detail-status">
        <span className="eval-spinner" />
        Loading run…
      </div>
    );
  }
  if (!run) {
    return (
      <div className="eval-empty">
        No evaluation runs yet. Run one over the golden set to see retrieval and answer-quality
        metrics.
      </div>
    );
  }

  // Running: a progress view. progress.total can be 0 before the harness has
  // counted the golden set — guard the percentage so the bar reads empty then.
  if (run.status === 'running') {
    const { done, total } = run.progress;
    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
    return (
      <div className="eval-detail-card">
        <div className="eval-running-head">
          <span className="eval-spinner" />
          <span className="eval-running-label">Evaluation in progress…</span>
        </div>
        <div className="eval-progress">
          <div className="eval-progress-track">
            <div className="eval-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="eval-progress-count">
            {done} / {total} queries
          </span>
        </div>
        <p className="eval-running-config">{pipelineSummary(run)}</p>
      </div>
    );
  }

  const metrics = run.metrics;
  const retrievalKeys = metrics ? Object.keys(metrics.retrieval) : [];

  return (
    <div className="eval-detail-card">
      {run.status === 'failed' && (
        <div className="eval-run-error">{run.error || 'The evaluation run failed.'}</div>
      )}

      <SummaryStrip run={run} />

      {metrics && retrievalKeys.length > 0 && (
        <div className="eval-metrics-grid">
          {retrievalKeys.map((key) => {
            const metric = metrics.retrieval[key];
            return (
              <div key={key} className="eval-metric-card">
                <div className="eval-metric-name" title={prettifyMetricKey(key)}>
                  {prettifyMetricKey(key)}
                </div>
                <div className="eval-metric-mean">{formatMetric(metric.mean, 3)}</div>
                {metric.ci && (
                  <div className="eval-metric-ci">
                    [{formatMetric(metric.ci.lo, 3)}, {formatMetric(metric.ci.hi, 3)}]
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {metrics?.judge && (
        <div className="eval-judge-wrap">
          <div className="eval-subhead">Answer quality (judge, 1-5)</div>
          <JudgeCard judge={metrics.judge} />
        </div>
      )}

      {run.queries && run.queries.length > 0 && (
        <div className="eval-query-wrap">
          <div className="eval-subhead">Per-query results ({run.queries.length})</div>
          <div className="eval-table-card">
            <table className="eval-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th> </th>
                  <th className="eval-num">Recall</th>
                  <th className="eval-num">nDCG</th>
                  <th className="eval-num">MRR</th>
                  <th className="eval-num">Faith.</th>
                  <th className="eval-num">Correct.</th>
                </tr>
              </thead>
              <tbody>
                {run.queries.map((query) => (
                  <QueryRow key={query.queryId} query={query} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
